import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createOffice, fetchOffices } from "../../redux/actions/officeActions";
import {
  FiPlus,
  FiCheck,
  FiArrowLeft,
  FiMapPin,
  FiAlertCircle,
  FiNavigation,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";

const CreateOffice = () => {
  const [formData, setFormData] = useState({
    office_name: "",
    latitude: "",
    longitude: "",
  });

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { creating, error, success } = useSelector((state) => state.office);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.office_name.trim()) return;
    dispatch(createOffice(formData));
  };

  useEffect(() => {
    if (!creating && !error && success) {
      dispatch(fetchOffices());
      setFormData({ office_name: "", latitude: "", longitude: "" });
    }
  }, [creating, error, success]);

  if (creating) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400 text-lg">
            Creating Office...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate("/dashboard/view-offices")}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <FiArrowLeft className="text-lg" />
            Back to Offices
          </button>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="bg-gradient-to-r from-cyan-500 to-blue-500 px-6 py-8">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-xl">
                <FiMapPin className="text-2xl text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white mb-2">
                  Create Office
                </h1>
                <p className="text-cyan-100">
                  Register a new head office or branch location
                </p>
              </div>
            </div>
          </div>

          <div className="p-6">
            {success && (
              <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-xl flex items-center gap-3">
                <FiCheck className="text-green-500" />
                <p className="text-green-800 dark:text-green-300 font-medium">
                  Office created successfully!
                </p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-4">
                {/* Office Name */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                    <FiMapPin className="text-sm" /> Office Name *
                  </label>
                  <input
                    name="office_name"
                    type="text"
                    value={formData.office_name}
                    onChange={handleChange}
                    placeholder="e.g. Bangalore Head Office"
                    className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-transparent rounded-xl py-3 px-4 text-gray-900 dark:text-white focus:ring-2 focus:ring-cyan-500 focus:bg-white transition-all placeholder-gray-400"
                    required
                  />
                </div>

                {/* Coordinates Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                      <FiNavigation className="text-sm" /> Latitude
                    </label>
                    <input
                      name="latitude"
                      type="text"
                      value={formData.latitude}
                      onChange={handleChange}
                      placeholder="12.9716"
                      className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-transparent rounded-xl py-3 px-4 text-gray-900 dark:text-white focus:ring-2 focus:ring-cyan-500 focus:bg-white transition-all placeholder-gray-400"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                      <FiNavigation className="text-sm" /> Longitude
                    </label>
                    <input
                      name="longitude"
                      type="text"
                      value={formData.longitude}
                      onChange={handleChange}
                      placeholder="77.5946"
                      className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-transparent rounded-xl py-3 px-4 text-gray-900 dark:text-white focus:ring-2 focus:ring-cyan-500 focus:bg-white transition-all placeholder-gray-400"
                    />
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-4 pt-6 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() =>
                    setFormData({
                      office_name: "",
                      latitude: "",
                      longitude: "",
                    })
                  }
                  className="bg-gray-500 hover:bg-gray-600 text-white py-3 px-6 rounded-xl transition-colors"
                >
                  Clear Form
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-cyan-500 to-blue-500 text-white py-3 px-6 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all"
                >
                  Create Office
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateOffice;
